/**
 * useCoverageEffect — Effect 2 of the /map state machine.
 *
 * Spec: docs/superpowers/specs/2026-05-07-map-state-machine.md §7.3
 * Plan: docs/superpowers/plans/2026-05-07-sprint-2026-21-map-refactor.md Task 5
 *
 * Responsibility:
 *   When `(activeScope, activeRasterMode, selectedDate)` is fully populated
 *   AND a Product is derivable from the raster mode, fetch
 *   /api/v1/satellite/coverage and populate `coverage` for the
 *   (scope_type, scope_id, product, date) cache key.
 *
 *   The coverage map drives the no-data-stripes filter on the map (§6.2,
 *   I-5) and the "X / Y fields covered" indicator pill.
 *
 * Cache key encoding:
 *   `${type}:${id ?? 'null'}:${product}:${date}` — same convention as
 *   datesEffect (T4) for the dates cache, just with date appended. Coverage
 *   is keyed per (scope, product, date) tuple; a single product holds many
 *   dates.
 *
 * Reducer surface (T2):
 *   COVERAGE_FETCH_START   → coverageStatus = { kind: 'loading' }
 *   COVERAGE_FETCH_SUCCESS → coverage[key] = data, status idle
 *   COVERAGE_FETCH_ERROR   → coverageStatus = { kind: 'error', message }
 *
 *   All three exist in mapState.MapAction (unlike DATES_FETCH which has only
 *   _SUCCESS), so the coverage lifecycle has full UI surface for the
 *   loading spinner and error toast described in spec §8.
 *
 * Cleanup:
 *   AbortController per fetch — unmount or dependency change cancels in-
 *   flight requests. AbortError is silently ignored (not surfaced as an
 *   error).
 *
 * StrictMode / dedup:
 *   Module-scoped in-flight Set guards against double-dispatch under React
 *   18 StrictMode (mount → cleanup → mount synchronously) and parallel
 *   mounts of the hook. The guard is keyed by the same string used for the
 *   coverage cache, so once a fetch is in flight a second concurrent call
 *   for the same tuple is a no-op. The guard is cleared in .finally(); the
 *   abort cleanup intentionally does NOT clear it (see datesEffect.ts T4
 *   comment for the StrictMode reasoning).
 */
import { useEffect } from 'react';
import { useMapStore } from '../useMapStoreV2';
import { productOf } from '../derived/productOf';
import type { CoverageData, CoverageKey } from '../state/mapState';

interface CoverageResponse {
  scope: {
    type: string;
    id: string | null;
    date: string;
    product: string;
  };
  covered_field_ids: string[];
  total_fields: number;
  covered_count: number;
}

/**
 * In-flight fetch guard. Module-scoped — survives React StrictMode's
 * double-invocation of effects in dev, rapid re-renders during dispatch
 * cascades, and parallel mounts of the hook in tests. Once a key resolves
 * it is removed; the result lands in `coverage` Map and subsequent calls
 * take the cache-hit branch.
 */
const inFlight = new Set<CoverageKey>();

export function useCoverageEffect(): void {
  // Per-field selectors avoid the new-object-every-render trap of
  // useMapStore() — each subscription fires only when its slice changes.
  const activeScope = useMapStore((s) => s.activeScope);
  const rasterMode = useMapStore((s) => s.activeRasterMode);
  const selectedDate = useMapStore((s) => s.selectedDate);
  const coverage = useMapStore((s) => s.coverage);
  const dispatch = useMapStore((s) => s.dispatch);

  useEffect(() => {
    const product = productOf(rasterMode);
    // Guard: any of (product, date) missing → no fetch. Spec §7.3 entry
    // condition. selectedDate=null is the post-CLOSE_SAT_PANEL state and
    // also occurs after SET_ACTIVE_SCOPE before datesEffect auto-selects.
    if (!product || !selectedDate) return;

    const key: CoverageKey = `${activeScope.type}:${activeScope.id ?? 'null'}:${product}:${selectedDate}`;

    // Cache hit: skip network entirely.
    if (coverage.has(key)) return;

    // Dedup concurrent requests for the same key (StrictMode, rapid re-renders).
    if (inFlight.has(key)) return;
    inFlight.add(key);

    const ac = new AbortController();
    const params = new URLSearchParams({
      scope_type: activeScope.type,
      product,
      date: selectedDate,
    });
    if (activeScope.id) params.set('scope_id', activeScope.id);

    dispatch({ type: 'COVERAGE_FETCH_START', key });

    fetch(`/api/v1/satellite/coverage?${params.toString()}`, {
      signal: ac.signal,
      credentials: 'include',
    })
      .then((r) => {
        if (!r.ok) throw new Error(`coverage ${r.status}`);
        return r.json() as Promise<CoverageResponse>;
      })
      .then((body) => {
        const data: CoverageData = {
          coveredFieldIds: new Set(body.covered_field_ids),
          totalFields: body.total_fields,
          fetchedAt: Date.now(),
        };
        dispatch({ type: 'COVERAGE_FETCH_SUCCESS', key, data });
      })
      .catch((err: unknown) => {
        // AbortError is expected on unmount / dep change — swallow.
        if (err instanceof Error && err.name === 'AbortError') return;
        const message = err instanceof Error ? err.message : String(err);
        dispatch({ type: 'COVERAGE_FETCH_ERROR', key, message });
      })
      .finally(() => {
        inFlight.delete(key);
      });

    // Cleanup aborts the in-flight fetch but does NOT clear inFlight here —
    // .finally() handles that once the promise settles. See datesEffect.ts
    // (T4) for the StrictMode mount-cleanup-mount reasoning.
    return () => ac.abort();
    // Dependency rationale:
    //   - activeScope.{type,id} primitives over the object reference —
    //     SET_ACTIVE_SCOPE always returns a new object, but the cache key
    //     only depends on type+id.
    //   - rasterMode for product derivation.
    //   - selectedDate for the date axis.
    //   - coverage so a previous COVERAGE_FETCH_SUCCESS that lands while
    //     the same (scope, product, date) is still active correctly takes
    //     the cache-hit branch on the next render.
    //   - dispatch for completeness (stable reference; no churn).
  }, [activeScope.type, activeScope.id, rasterMode, selectedDate, coverage, dispatch]);
}
